import React from 'react';
import { Alert, Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Warning as WarningIcon, Error as ErrorIcon } from '@mui/icons-material';
import InfoIcon from "@/assets/svgs/info.svg";
import { CreateProductInput } from '@/services/product-catalogue/pc-api';
import { tokens } from "@/tokens";

interface ValidationAlertsProps {
  product: CreateProductInput;
  index: number;
  requiresMetalPercentage?: boolean;
}

export interface ValidationIssue {
  type: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
}

export const validateProduct = (product: CreateProductInput, requiresMetalPercentage?: boolean): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  // Required field validation
  if (!product.sku) {
    issues.push({ type: 'error', message: 'Product SKU is required', field: 'sku' });
  }
  if (!product.name) {
    issues.push({ type: 'error', message: 'Product Name is required', field: 'name' });
  }
  if (!product.htsCode) {
    issues.push({ type: 'error', message: 'HTS Code is required', field: 'htsCode' });
  }
  if (!product.countryOfOrigin) {
    issues.push({ type: 'error', message: 'Country of Origin is required', field: 'countryOfOrigin' });
  }

  // HTS Code format validation
  if (product.htsCode && !/^\d{4}\.\d{2}\.\d{4}$/.test(product.htsCode)) {
    issues.push({ 
      type: 'warning', 
      message: 'HTS Code format should be XXXX.XX.XXXX', 
      field: 'htsCode' 
    });
  }

  // Country code validation (should be 2 letters)
  if (product.countryOfOrigin && !/^[A-Z]{2}$/.test(product.countryOfOrigin)) {
    issues.push({ 
      type: 'warning', 
      message: 'Country code should be 2 uppercase letters (e.g., US, CN)', 
      field: 'countryOfOrigin' 
    });
  }

  // Metal percentage validation
  if (requiresMetalPercentage && (product.metalPercentage === undefined || product.metalPercentage === null)) {
    issues.push({ 
      type: 'warning', 
      message: 'Metal percentage is required for this HTS code/country combination', 
      field: 'metalPercentage' 
    });
  }

  if (product.metalPercentage !== undefined && product.metalPercentage !== null) {
    if (product.metalPercentage < 0 || product.metalPercentage > 100) {
      issues.push({ 
        type: 'error', 
        message: 'Metal percentage must be between 0 and 100', 
        field: 'metalPercentage' 
      });
    }
  }

  return issues;
};

const ValidationAlerts: React.FC<ValidationAlertsProps> = ({ 
  product, 
  index, 
  requiresMetalPercentage 
}) => {
  const issues = validateProduct(product, requiresMetalPercentage);

  if (issues.length === 0) {
    return null;
  }

  const errors = issues.filter(issue => issue.type === 'error');
  const warnings = issues.filter(issue => issue.type === 'warning');
  const infos = issues.filter(issue => issue.type === 'info');

  return (
    <Box sx={{ mt: 2 }}>
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} gutterBottom>
            Row {index + 1} - Errors:
          </Typography>
          <List dense sx={{ py: 0 }}>
            {errors.map((issue, idx) => (
              <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <ErrorIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={issue.message}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} gutterBottom>
            Row {index + 1} - Warnings:
          </Typography>
          <List dense sx={{ py: 0 }}>
            {warnings.map((issue, idx) => (
              <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <WarningIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={issue.message}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {infos.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} gutterBottom>
            Row {index + 1} - Info:
          </Typography>
          <List dense sx={{ py: 0 }}>
            {infos.map((issue, idx) => (
              <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <img src={InfoIcon} alt="Info" style={{ width: 20, height: 20 }} />
                </ListItemIcon>
                <ListItemText 
                  primary={issue.message}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </Box>
  );
};

export default ValidationAlerts;